import React, {
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";

import useStructureParams from "../lib/useStructureParams";
import { useCrunchCanvassingLeader } from "../data/useCrunchCanvassingLeader";

import PageTitle from "./PageTitle";
import Breadcrumbs from "./Breadcrumbs";
import PageHeader from "./PageHeader";
import ViewButtons from "./ViewButtons";
import { Col, Row, Button } from "react-bootstrap";

import { LoadingSpinner } from "./spinners";
import { NoAssignedWardsMessage } from "./alerts";

import CustomCard from "./CustomCard";
import CardDeck from "react-bootstrap/CardDeck";

// import moment from "moment";

import VDCategoryFilter from "./VDCategoryFilter";
import { DashboardContext } from "../data/context";

import { percColour } from "../lib/utils";

import { AgGridReact } from "ag-grid-react";
import {
  ValueFormatterParams,
  GridOptions,
  CellStyle,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import _ from "lodash";

import useUserStructures from "../data/useUserStructures";

import HeaderLayout from "./HeaderLayout";

interface AGParams {
  prov_name: string;
  prov_code: string;
  const_code?: string;
  const_name?: string;
  region_code?: string;
  region_name?: string;
  ward_code?: string;
  ward_name?: string;
  perc: number;
  country_rank: number;
  province_rank?: number;
  constituency_rank?: number;
}

type DetailKey =
  | "province_detail"
  | "region_detail"
  | "constituency_detail"
  | "big_ward_detail"
  | "medium_ward_detail"
  | "small_ward_detail";

export default function CrunchCanvassingLeaderView(): JSX.Element {
  const { filterLabel } = useContext(DashboardContext);
  // get parameters from the URL
  const { code, type } = useStructureParams();

  // get stats from the GraphQL API
  const { stats, loading } = useCrunchCanvassingLeader({
    type,
    code,
    filterLabel,
  });

  //Limit onclick access to "country" users
  //Used in the ag grid table
  const hasAccess = useUserStructures()?.[0]?.type || "";

  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  //State to filter tabs and ag grid data set
  const [selectedHeader, setSelectedHeader] = useState<string>("Province");
  const [selectedTable, setSelectedTable] =
    useState<DetailKey>("province_detail");

  //reset ag grid col filters
  const gridRef = useRef<AgGridReact>(null);

  const resetAGFilters = useCallback(() => {
    gridRef?.current?.columnApi.resetColumnState();
    gridRef?.current?.api.setFilterModel(null);
  }, []);

  // this happens while the query is still fetching
  if (stats == null || loading) {
    return <LoadingSpinner />;
  }

  //data for all table versions (ag grid)
  const allTableData = {
    province_detail: stats?.crunchWeekCanvassingLeaderboard?.province_detail,
    constituency_detail: stats?.crunchWeekCanvassingLeaderboard?.constituency_detail,
    region_detail: stats?.crunchWeekCanvassingLeaderboard?.region_detail,
    big_ward_detail: stats?.crunchWeekCanvassingLeaderboard?.big_ward_detail,
    medium_ward_detail: stats?.crunchWeekCanvassingLeaderboard?.medium_ward_detail,
    small_ward_detail: stats?.crunchWeekCanvassingLeaderboard?.small_ward_detail,
  };

  //extract date for data
  // const extractedDate =
    // stats?.crunchWeekCanvassingLeaderboard?.province_detail[0].extract_date;
  //console.log("[extractedDate]", extractedDate);

  //dynamic cols for ag grid
  const createTableCols = () => {
    const filteredCols = _.uniq(_.flatMap(allTableData[selectedTable], _.keys))
      .filter((item: string) => item !== "__typename")
      .map((item: string) => ({
        field: item,
        filter:
          item === "prov_name" ||
          item === "const_name" ||
          item === "region_name" ||
          item === "ward_name"
            ? true
            : false,
        filterParams: {
          filterOptions: ["contains"],
          defaultOption: "contains",
          maxNumConditions: 1,
        },
        headerName: item
          .replaceAll("prov_", "province")
          .replaceAll("name", "")
          .replaceAll("const_", "constituency ")
          .replaceAll("perc", "% of Crunch Week Goal Met")
          .replaceAll("_", " "),
        //.toUpperCase(),
        headerClass: "crunch-lead-filter-header",
        minWidth: item === "prov_name" ? 35 : 100,
        //cellClass: "ag-cell-mobi",
        wrapText: true,
        //flex: 1,
        autoHeight: true,
        sortable: true,
        tooltipField: item,
        hide:
          item === "prov_code" ||
          item === "const_code" ||
          item === "region_code" ||
          item === "ward_code" 

            ? true
            : false,
      }));

    return filteredCols;
  };

  //data for dynamic tabs
  const tileData = stats?.crunchWeekCanvassingLeaderboard;
  //console.log("tileData", tileData);

  //function to get rank through filtering
  const rank = (key: number, data: any[], rank: number) => {
    let filterName: string;

    if (key === 0) {
      filterName = "prov_name";
    } else if (key === 1) {
      filterName = "region_name";
    } else if (key === 2) {
      filterName = "const_name";
    } else {
      filterName = "ward_name";
    }

    return data
      .filter((item) => item.country_rank === rank)
      .map((item) => ({
        name: item[filterName],
        perc: item.perc,
      }));
  };

  //create tabs with appropriate data format
  const generateTabs = () => {
    const tabHeaders = [
      "Province",
      "Region Country",
      "Constituency Country",
      "Big Ward Country",
      "Medium Ward Country",
      "Small Ward Country",
    ];

    const formattedTabs = [];

    for (let i = 0; i < tabHeaders.length; i++) {
      let rankData =
        tileData[
          `${tabHeaders[i]
            .toLowerCase()
            .replaceAll(" country", "")
            .replaceAll(" ", "_")}_detail` as keyof typeof tileData
        ];

      formattedTabs.push({
        id: i,
        key: `${tabHeaders[i].toLowerCase().replaceAll(" country", "").replaceAll(" ", "_")}_detail`,
        header: tabHeaders[i].replaceAll("Country", ""),
        first: rank(i, rankData, 1),
        second: rank(i, rankData, 2),
        third: rank(i, rankData, 3),
      });
    }

    return formattedTabs;
  };

  console.log("generateTabs", generateTabs());

  //scroll to an element
  const scrollToTable = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  //filter ag grid source based off selected tab
  const filterTabs = (header: string, key: DetailKey) => {
    setSelectedHeader(header);
    setSelectedTable(key);
    resetAGFilters();
    scrollToTable("table");
  };

  //ag grid options
  const gridOptions: GridOptions = {
    icons: {
      menu: '<span class="ag-icon ag-icon-filter"></span>',
    },
  };

  if (!stats.structureHasAssignedWards) {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix="/crunchweek-leader" />
        <HeaderLayout
          element1={<PageHeader structure={stats} />}
          element2={<ViewButtons structure={stats} section="crunchweeklead" />}
        />
        <VDCategoryFilter counts={stats.counts} />
        <NoAssignedWardsMessage type={stats.type} />
      </>
    );
  } else {
    return (
      <div className="page-wrapper-crunch-lead">
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix="/crunchweek-leader" />
        {/* <VDCategoryFilter counts={stats.counts} /> */}
        <HeaderLayout
          element1={<PageHeader structure={stats} />}
          element2={<ViewButtons structure={stats} section="crunchweeklead" />}
        />

        <Row style={{ marginBottom: 15 }}>
          <Col md={12}>
            <CardDeck>
              {generateTabs().map((card: any) => (
                <CustomCard
                  key={card.id}
                  header={card.header}
                  first={card.first}
                  second={card.second}
                  third={card.third}
                  cardFunction={() => filterTabs(card.header, card.key)}
                  selectedCard={selectedHeader}
                />
              ))}
            </CardDeck>
          </Col>
        </Row>

        <Row style={{ flex: 1 }} id="table">
          <Col md={12}>
            <div
              className="ag-theme-alpine ag-data-table ag-grid-table-mobi"
              style={{ width: "100%", height: "100%" }}
            >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <h6 style={{ color: "#155fa2" }}>
                  {selectedHeader} Rank Table
                </h6>
                <Button
                  className="ag-ri-reset-btn"
                  onClick={() => resetAGFilters()}
                >
                  Reset Table
                </Button>
              </div>

              <AgGridReact
                rowData={allTableData[selectedTable]}
                gridOptions={gridOptions}
                columnDefs={createTableCols()}
                ref={gridRef}
                defaultColDef={{
                  flex: 1,
                  unSortIcon: true,
                  suppressMovable: true,
                  cellStyle: (
                    params: ValueFormatterParams<AGParams, any>,
                  ): CellStyle => {
                    if (params.colDef.field === "perc") {
                      return {
                        color: "#fff",
                        fontWeight: "bold",
                        backgroundColor: percColour(params.value),
                        textAlign: "center",
                        cursor: "default",
                        wordBreak: "normal",
                      };
                    }

                    return { wordBreak: "normal" };
                  },
                  cellClass: (params: ValueFormatterParams<AGParams, any>) => {
                    if (
                      (params.colDef.field === "prov_name" ||
                        params.colDef.field === "const_name" ||
                        params.colDef.field === "region_name" ||
                        params.colDef.field === "ward_name") &&
                      hasAccess === "country"
                    ) {
                      return "ag-grid-link-cell";
                    }

                    return "ag-grid-global-cell";
                  },
                  valueFormatter: (
                    params: ValueFormatterParams<AGParams, any>,
                  ) => {
                    if (params.colDef.field === "perc") {
                      return `${params.value.toFixed(1)}%`;
                    } else if (params.colDef.field === "country_rank") {
                      return `#${params.value} in South Africa`;
                    } else if (params.colDef.field === "province_rank") {
                      return `#${params.value} in ${params.data?.prov_name}`;
                    } else if (params.colDef.field === "constituency_rank") {
                      return `#${params.value} in ${params.data?.const_name}`;
                    } else if (
                      width < 768 &&
                      params.colDef.field === "prov_name"
                    ) {
                      return params.data?.prov_code;
                    } else {
                      return params.value;
                    }
                  },
                }}
                onCellClicked={(e) => {
                  if (
                    e.colDef.headerName?.toLowerCase() === "province" &&
                    hasAccess === "country"
                  ) {
                    window.open(
                      `/province/${e.data?.prov_code}/crunchweek`,
                      "_blank",
                    );
                  }

                  if (
                    e.colDef.headerName?.toLowerCase() === "constituency " &&
                    hasAccess === "country"
                  ) {
                    window.open(
                      `/constituency/${e.data?.const_code}/crunchweek`,
                      "_blank",
                    );
                  }

                  if (
                    e.colDef.headerName?.toLowerCase() === "region " &&
                    hasAccess === "country"
                  ) {
                    window.open(
                      `/region/${e.data?.region_code}/crunchweek`,
                      "_blank",
                    );
                  }

                  if (
                    e.colDef.headerName?.toLowerCase() === "ward " &&
                    hasAccess === "country"
                  ) {
                    window.open(
                      `/ward/${e.data?.ward_code}/crunchweek`,
                      "_blank",
                    );
                  }
                }}
                suppressMenuHide={true}
              />
            </div>
          </Col>
        </Row>

        {/* <Row style={{ marginTop: 38 }}>
          <Col md={12}>
            <p>
              Data last updated on:{" "}
              <span style={{ color: "#155fa2", fontWeight: "bold" }}>
                {moment(extractedDate).format("ddd, D MMM yyyy HH:mm A")}
              </span>
            </p>
          </Col>
        </Row> */}
      </div>
    );
  }
}
